import type { ReactElement, ReactNode } from 'react'
import { useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import cc from 'classcat'

import { getPlain } from '../../store/utils'
import { useTranslate } from '../../utils/translate'
import useEffectExceptMount from '../../utils/hooks/useEffectExceptMount'

type NotificationShape = {
  id?: string
  type?: string
  message?: ReactNode
}

// evil hack to retain cross mount state (this component remounts since each route transition remounts the complete theme)
let runningTimeoutId: number | undefined

export default function Notification({
  duration = 8000,
  className,
}: Readonly<{ duration?: number; className?: string }>): ReactElement | null {
  const [visible, setVisible] = useState(Boolean(runningTimeoutId))
  const [showMessage, setShowMessage] = useState(false)

  const notification = getPlain(
    useSelector<State, NotificationShape>((state) => state.getIn(['view', 'themeNotification'], {})),
  )

  const t = useTranslate('shop')

  const { id, type, message } = notification

  const hide = useCallback(() => {
    setVisible(false)
    runningTimeoutId = undefined
  }, [])

  useEffect(() => {
    if (runningTimeoutId) {
      clearTimeout(runningTimeoutId)
      runningTimeoutId = window.setTimeout(hide, duration)
    }

    return () => clearTimeout(runningTimeoutId)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // This effect is used to ensure that the message is announced to screen readers
  // after a short delay, which is necessary for live regions to work correctly:
  // https://tetralogical.com/blog/2024/05/01/why-are-my-live-regions-not-working/
  useEffect(() => {
    let messageTimeout: number | undefined
    if (visible) {
      messageTimeout = window.setTimeout(() => {
        setShowMessage(true)
      }, 200)
    } else {
      setShowMessage(false)
    }
    return () => {
      if (messageTimeout) clearTimeout(messageTimeout)
    }
  }, [visible])

  // if there's a timer running while a new notification is displayed then
  // prevent this old timer from hiding the new notification (too soon / instantly)
  useEffectExceptMount(() => {
    clearTimeout(runningTimeoutId)
    runningTimeoutId = window.setTimeout(hide, duration)

    setVisible(Boolean(id))

    return () => clearTimeout(runningTimeoutId)
  }, [duration, hide, id])

  return (
    <div
      className={cc([
        className,
        'add-to-cart-notice',
        {
          'add-to-cart-notice-visible': visible,
          'add-to-cart-notice-hidden': !visible,
          [`add-to-cart-notice-${type}`]: type,
        },
      ])}
      onClick={hide}
    >
      <div className="add-to-cart-notice-wrapper" role="status" aria-live="assertive">
        {message && <span className="add-to-cart-notice-message">{showMessage ? message : null}</span>}
        <span className="add-to-cart-notice-close" title={t('notifications.dismissButton.tooltip')} />
      </div>
    </div>
  )
}
